import { useState } from "react";
import { OccurrenceStepEnum } from "../enums/occurrenceStep";
import { OccurrenceWithTimeSpent } from "../models/occurrence";
import { useMediaQuery } from "@mui/material";

export interface KanbanChartsProps {
    totalOccurrences: number;
    occurrenceWithTimeSpentList: OccurrenceWithTimeSpent[];
    finalizedOccurrenceWithTimeSpentList: OccurrenceWithTimeSpent[];
    finishedFilter: string[];
    stepFilter: OccurrenceStepEnum | null;
    chassisListFilterByStep: string[];
}

export function useKanbanCharts(props: KanbanChartsProps) {
    const [activeStepTab, setActiveStepTab] = useState('');
    const [selectedMetric, setSelectedMetric] = useState<"average" | "quantity">("average");
    const [timePeriod, setTimePeriod] = useState<"daily" | "monthly">("daily");

    const uniqueDays = [...new Set(props.occurrenceWithTimeSpentList?.map((item) => item.currentStepStart?.toISOString().split('T')[0]))].filter(Boolean).sort();
    const monthAndYears = [...new Set(props.occurrenceWithTimeSpentList?.map((item) => item.currentStepStart?.toISOString().split('T')[0].split('-').slice(0, 2).join('-')))].filter(Boolean).sort();

    let occurrencesByDays: { [key in OccurrenceStepEnum]: { daily: { date: string, occurrences: any[] }[], monthly: { date: string, occurrences: any[] }[] } } = {} as any;
    Object.keys(OccurrenceStepEnum).forEach((step) => {
        occurrencesByDays[OccurrenceStepEnum[step as keyof typeof OccurrenceStepEnum]] = {
            daily: uniqueDays.map((day) => ({
                date: day,
                occurrences: props.occurrenceWithTimeSpentList.filter((item) => item.currentStepStart?.toISOString().split('T')[0] === day && item.currentStep === OccurrenceStepEnum[step as keyof typeof OccurrenceStepEnum]),
            })),
            monthly: monthAndYears.map((monthAndYear) => ({
                date: monthAndYear,
                occurrences: props.occurrenceWithTimeSpentList.filter((item) => item.currentStepStart?.toISOString().split('T')[0].split('-').slice(0, 2).join('-') === monthAndYear && item.currentStep === OccurrenceStepEnum[step as keyof typeof OccurrenceStepEnum]),
            })),
        };
    });

    let currentOccurrencesBySteps: { [key in OccurrenceStepEnum]: OccurrenceWithTimeSpent[] } = {} as any;
    Object.keys(OccurrenceStepEnum).forEach((step) => {
        currentOccurrencesBySteps[OccurrenceStepEnum[step as keyof typeof OccurrenceStepEnum]] = props.occurrenceWithTimeSpentList.filter((item) => item.currentStep === OccurrenceStepEnum[step as keyof typeof OccurrenceStepEnum]);
    });
    let finalizedOccurrencesBySteps: { [key in OccurrenceStepEnum]: OccurrenceWithTimeSpent[] } = {} as any;
    Object.keys(OccurrenceStepEnum).forEach((step) => {
        finalizedOccurrencesBySteps[OccurrenceStepEnum[step as keyof typeof OccurrenceStepEnum]] = props.finalizedOccurrenceWithTimeSpentList.filter((item) => item.steps[OccurrenceStepEnum[step as keyof typeof OccurrenceStepEnum]]);
    });
    let allOccurrencesBySteps: { [key in OccurrenceStepEnum]: OccurrenceWithTimeSpent[] } = {} as any;
    Object.keys(OccurrenceStepEnum).forEach((step) => {
        allOccurrencesBySteps[OccurrenceStepEnum[step as keyof typeof OccurrenceStepEnum]] = props.occurrenceWithTimeSpentList.filter((item) => item.steps[OccurrenceStepEnum[step as keyof typeof OccurrenceStepEnum]]);
    });

    const occurrencesWithTimeSpentFilteredByStatus = props.occurrenceWithTimeSpentList
        .filter((item) => {
            if(props.finishedFilter.includes('true') && props.finishedFilter.includes('false')) return true;
            if(props.finishedFilter.includes('true')) return !item.currentStep;
            if(props.finishedFilter.includes('false')) return item.currentStep;
            return true;
        });

    // Get step filter based on active tab and selected metric
    const stepFilter = () => (activeStepTab && selectedMetric === 'average') ?
        'average' : (activeStepTab && selectedMetric === 'quantity') ?
            'quantity' : null;

    // Get big numbers based on finished filter
    const getBigNumbers = () => {

        if(stepFilter() === 'average') {
            const chassisListFilter: string[] = props.chassisListFilterByStep || [];
            const occurrences = occurrencesWithTimeSpentFilteredByStatus.filter(item => chassisListFilter.length === 0 || chassisListFilter.includes(item.chassis)) 
            
            const timeSpents = occurrences.map(item => item.timeSpent);
            const totalOfInProgressOccurrence = occurrences.filter(item => item.currentStep).length;
            const totalOfFinalizedOccurrence = occurrences.filter(item => !item.currentStep).length;
            const totalOfNotStartedOccurrence = occurrences.length - totalOfInProgressOccurrence - totalOfFinalizedOccurrence;

            return {
                totalOfInProgressOccurrence,
                totalOfFinalizedOccurrence,
                totalOfNotStartedOccurrence,
                totalInService: timeSpents.reduce((acc, curr) => acc + curr, 0),
                averageInService: timeSpents.length > 0 ? timeSpents.reduce((acc, curr) => acc + curr, 0) / timeSpents.length : 0,
            }
        }

        if(stepFilter() === 'quantity') {
            const occurrences = occurrencesWithTimeSpentFilteredByStatus.filter(item => item.currentStep === activeStepTab);
            
            const timeSpents = occurrences.map(item => item.timeSpent);
            const totalOfInProgressOccurrence = occurrences.filter(item => item.currentStep).length;
            const totalOfFinalizedOccurrence = occurrences.filter(item => !item.currentStep).length;
            const totalOfNotStartedOccurrence = occurrences.length - totalOfInProgressOccurrence - totalOfFinalizedOccurrence;

            return {
                totalOfInProgressOccurrence,
                totalOfFinalizedOccurrence,
                totalOfNotStartedOccurrence,
                totalInService: timeSpents.reduce((acc, curr) => acc + curr, 0),
                averageInService: timeSpents.length > 0 ? timeSpents.reduce((acc, curr) => acc + curr, 0) / timeSpents.length : 0,
            }
        }
        
        const timeSpents = occurrencesWithTimeSpentFilteredByStatus.map(item => item.timeSpent);
        const inProgressOccurrences = occurrencesWithTimeSpentFilteredByStatus.filter(item => item.currentStep);
        const finalizedOccurrences = occurrencesWithTimeSpentFilteredByStatus.filter(item => !item.currentStep);
        const totalOfNotStartedOccurrence = props.totalOccurrences - inProgressOccurrences.length - finalizedOccurrences.length;
        
        return {
            totalOfInProgressOccurrence: inProgressOccurrences.length,
            totalOfFinalizedOccurrence: finalizedOccurrences.length,
            totalOfNotStartedOccurrence,
            totalInService: timeSpents.reduce((acc, curr) => acc + curr, 0),
            averageInService: timeSpents.length > 0 ? timeSpents.reduce((acc, curr) => acc + curr, 0) / timeSpents.length : 0,
        }
    }

    const isResponsiveView = useMediaQuery('(max-width: 600px)');
    const chartWidthMultiplier = isResponsiveView ? 80 : 100;
    const defaultChartMinWidth = isResponsiveView ? 400 : 600;
    const dynamicBarSize = isResponsiveView ? 48 : 60;

    // Função para ajustar dados com altura mínima para barras
    const adjustDataWithMinHeight = (
        data: any[],
        minHeightPercentage: number = 5
    ) => {
        if (!data || data.length === 0) return data;

        const maxValue = Math.max(...data.map((item) => item.time));
        const minValue = Math.min(...data.map((item) => item.time));

        if (maxValue === minValue || (maxValue - minValue) / maxValue < 0.01) {
            if (maxValue <= 1) {
                return data.map((item, index) => ({
                    ...item,
                    time: item.time === 0 ? 0 : (8 + index * 1),
                    originalTime: item.time,
                }));
            }

            return data.map((item, index) => ({
                ...item,
                time: maxValue * (0.3 + index * 0.1),
                originalTime: item.time,
            }));
        }

        const minHeight = Math.max((maxValue * minHeightPercentage) / 100, 10);

        return data.map((item) => ({
            ...item,
            time: item.time === 0 ? 0 : Math.max(item.time, minHeight),
            originalTime: item.time,
        }));
    };

    const [showAverageTimeChart, setShowAverageTimeChart] = useState(true);
    const [showQuantityChart, setShowQuantityChart] = useState(true);

    const POINT_WIDTH = isResponsiveView ? 60 : 50;
    const MIN_WIDTH = isResponsiveView ? 500 : 600;
    const containerWidth = Math.max((occurrencesByDays[activeStepTab as keyof typeof occurrencesByDays]?.[timePeriod]?.length ?? 0) * POINT_WIDTH, MIN_WIDTH);
    const LEFT_LABEL_PAD = isResponsiveView ? 70 : 70;

    return {
        containerWidth,
        LEFT_LABEL_PAD,
        isResponsiveView,
        chartWidthMultiplier,
        defaultChartMinWidth,
        dynamicBarSize,
        showAverageTimeChart,
        setShowAverageTimeChart,
        showQuantityChart,
        setShowQuantityChart,
        selectedMetric,
        setSelectedMetric,
        activeStepTab,
        setActiveStepTab,
        timePeriod,
        setTimePeriod,

        currentOccurrencesBySteps,
        finalizedOccurrencesBySteps,
        allOccurrencesBySteps,
        occurrencesByDays,
        adjustDataWithMinHeight,

        getBigNumbers,
    }
}